import SalviaLayout from "@/components/SalviaLayout";
import ModuleAIChatWidget from "@/components/ModuleAIChatWidget";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { buildAIContextSnapshot } from "@/lib/aiScopeContext";
import { Activity, BarChart3, HeartPulse, ShieldAlert, Sparkles, TrendingUp } from "lucide-react";
import { useMemo } from "react";
import { useLocation } from "wouter";

const MODULE_LABELS: Record<string, string> = {
  nr1: "NR1",
  telemedicina: "Telemedicina",
  aps_fisica: "APS Física",
  aps_digital: "APS Digital",
  insights_ia: "Insights com IA",
};

function formatNumber(value: unknown) {
  return new Intl.NumberFormat("pt-BR").format(Number(value ?? 0));
}

function formatPercent(value: unknown) {
  return `${(Number(value ?? 0) * 100).toFixed(1).replace(".", ",")}%`;
}

function formatScore(value: unknown) {
  return Number(value ?? 0).toFixed(1).replace(".", ",");
}

export default function InsightsIAHubPage() {
  const [, setLocation] = useLocation();

  const contextSnapshot = useMemo(
    () => buildAIContextSnapshot(typeof window === "undefined" ? "/portal/salvia" : window.location.pathname),
    [],
  );

  const analyticsFilters = useMemo(() => {
    const now = new Date();
    const from = new Date(now);
    from.setDate(now.getDate() - 29);

    return {
      from: from.toISOString().slice(0, 10),
      to: now.toISOString().slice(0, 10),
    };
  }, []);

  const workspaceQuery = trpc.insights.getWorkspaceContext.useQuery(
    { surface: "hub", contextSnapshot },
    {
      staleTime: 60_000,
    },
  );

  const healthContextQuery = trpc.healthIndicators.context.useQuery(analyticsFilters, { staleTime: 60_000 });
  const healthOverviewQuery = trpc.healthIndicators.overview.useQuery(analyticsFilters, { staleTime: 60_000 });
  const healthTimelineQuery = trpc.healthIndicators.timeline.useQuery(analyticsFilters, { staleTime: 60_000 });
  const healthBreakdownsQuery = trpc.healthIndicators.breakdowns.useQuery({ ...analyticsFilters, limit: 5 }, { staleTime: 60_000 });

  const companySummary = workspaceQuery.data?.scope.hasCompanyContext
    ? workspaceQuery.data.scope.companyName || `Empresa ${workspaceQuery.data.scope.companyId}`
    : workspaceQuery.data?.accessEnvelope?.isAdminBypass
      ? "Visão geral da plataforma"
      : "Leitura geral disponível";

  const campaignSummary = workspaceQuery.data?.campaign
    ? `${workspaceQuery.data.campaign.status} · campanha ${workspaceQuery.data.campaign.id}`
    : "Sem campanha disponível agora";

  const summariesCount = workspaceQuery.data?.summaries.length || 0;
  const moduleReadiness = workspaceQuery.data?.moduleReadiness || [];
  const visibleModules = moduleReadiness.filter((item) => item.userHasVisibleAccess);
  const accessEnvelope = workspaceQuery.data?.accessEnvelope;
  const healthOverview = healthOverviewQuery.data;
  const healthBreakdowns = healthBreakdownsQuery.data;
  const timeline = (healthTimelineQuery.data || []) as Array<{
    metricDate?: string | number | Date | null;
    encountersTotal?: number | null;
    highRiskLives?: number | null;
  }>;

  const accessHeadline = accessEnvelope?.isAdminBypass
    ? "Visão ampla liberada"
    : accessEnvelope?.hub.contractEnabled
      ? "Leitura liberada"
      : "Leitura parcial";

  const kpiCards = useMemo(
    () => [
      {
        label: "Vidas elegíveis",
        value: formatNumber(healthOverview?.population?.eligibleLives),
        detail: `Usuários ativos: ${formatNumber(healthOverview?.population?.activeUsers)}`,
        icon: <HeartPulse className="h-4 w-4" />,
      },
      {
        label: "Atendimentos no período",
        value: formatNumber(healthOverview?.access?.encountersTotal),
        detail: `Agendamentos: ${formatNumber(healthOverview?.access?.appointmentsTotal)}`,
        icon: <Activity className="h-4 w-4" />,
      },
      {
        label: "Experiência média",
        value: formatScore(healthOverview?.experience?.avgScore),
        detail: `NPS: ${formatScore(healthOverview?.experience?.npsScore)}`,
        icon: <TrendingUp className="h-4 w-4" />,
      },
      {
        label: "Vidas em alto risco",
        value: formatNumber(healthOverview?.risk?.highRiskLives),
        detail: `Críticas: ${formatNumber(healthOverview?.risk?.criticalRiskLives)}`,
        icon: <ShieldAlert className="h-4 w-4" />,
      },
    ],
    [healthOverview],
  );

  const timelineTail = useMemo(() => {
    const recent = timeline.slice(-7);
    const maxEncounters = Math.max(...recent.map((item) => Number(item.encountersTotal ?? 0)), 1);

    return recent.map((item) => ({
      ...item,
      barWidth: `${Math.max((Number(item.encountersTotal ?? 0) / maxEncounters) * 100, 8)}%`,
    }));
  }, [timeline]);

  const assistantEnabled = Boolean(accessEnvelope?.widget.effectiveAccess || accessEnvelope?.hub.effectiveAccess);

  return (
    <SalviaLayout title="Insights com IA">
      <div className="space-y-4">
        <section className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-700">
                <Sparkles className="h-3.5 w-3.5 text-[#84cc16]" />
                Portal da Jornada do Cliente
              </div>
              <div className="space-y-2">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-950">Insights com IA</h1>
                <p className="max-w-3xl text-sm leading-6 text-slate-600">
                  Consulte rapidamente os sinais mais relevantes do escopo atual e use o assistente apenas quando precisar aprofundar a leitura.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setLocation("/portal")} variant="secondary" className="min-w-36">
                Voltar ao início
              </Button>
              <Button onClick={() => setLocation("/home/rh")} variant="outline" className="min-w-36">
                Abrir NR1
              </Button>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Seu acesso</p>
              <p className="mt-2 text-sm font-semibold text-slate-950">{accessHeadline}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Empresa em leitura</p>
              <p className="mt-2 text-sm font-semibold text-slate-950">{companySummary}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">NR1 agora</p>
              <p className="mt-2 text-sm font-semibold text-slate-950">{campaignSummary}</p>
            </div>
            <div className="rounded-2xl border border-[#C0FF00]/25 bg-[#F7FEE7] p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">Resumos disponíveis</p>
              <p className="mt-2 text-sm font-semibold text-slate-950">{workspaceQuery.isLoading ? "Carregando..." : `${summariesCount} prontos`}</p>
            </div>
          </div>
        </section>

        <section className="grid gap-4 xl:grid-cols-[minmax(0,1.12fr)_340px]">
          <article className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-700">
                  <BarChart3 className="h-3.5 w-3.5" />
                  Leitura executiva
                </div>
                <h2 className="text-lg font-semibold text-slate-950">Panorama executivo</h2>
                <p className="max-w-2xl text-sm leading-6 text-slate-600">
                  A tela mantém os sinais centrais de acesso, experiência e risco visíveis sem depender do chat aberto.
                </p>
              </div>
              <Badge variant="outline">Janela: últimos 30 dias</Badge>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {kpiCards.map((card) => (
                <div key={card.label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{card.label}</p>
                    <div className="rounded-xl bg-white p-2 text-slate-700 shadow-sm">{card.icon}</div>
                  </div>
                  <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                    {healthOverviewQuery.isLoading ? "--" : card.value}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-600">{card.detail}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Série recente</p>
                    <p className="mt-1 text-sm font-semibold text-slate-950">Atendimentos e alto risco</p>
                  </div>
                  <Badge variant="outline">{timelineTail.length} pontos</Badge>
                </div>
                <div className="mt-4 space-y-3">
                  {timelineTail.length ? timelineTail.map((item) => (
                    <div key={String(item.metricDate)} className="space-y-1">
                      <div className="flex items-center justify-between gap-3 text-xs text-slate-600">
                        <span>{String(item.metricDate)}</span>
                        <span>{formatNumber(item.encountersTotal)} atendimentos · {formatNumber(item.highRiskLives)} alto risco</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-200">
                        <div className="h-2 rounded-full bg-slate-900" style={{ width: item.barWidth }} />
                      </div>
                    </div>
                  )) : (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-3 py-4 text-xs leading-5 text-slate-600">
                      {healthTimelineQuery.isLoading ? "Carregando série temporal..." : "Ainda não há série suficiente para este recorte."}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <article className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Serviços com mais uso</p>
                  <div className="mt-3 space-y-2">
                    {healthBreakdowns?.services?.length ? healthBreakdowns.services.map((item) => (
                      <div key={String(item.serviceKey)} className="rounded-2xl bg-white px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-900">{String(item.serviceName ?? item.serviceKey)}</p>
                          <Badge variant="outline">{String(item.careModality ?? "")}</Badge>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-slate-600">
                          {formatNumber(item.encountersTotal)} atendimentos · no-show {formatPercent(item.noShowRate)}
                        </p>
                      </div>
                    )) : (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-3 py-3 text-xs leading-5 text-slate-600">
                        {healthBreakdownsQuery.isLoading ? "Carregando serviços..." : "Nenhum serviço consolidado neste recorte."}
                      </div>
                    )}
                  </div>
                </article>

                <article className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Risco em destaque</p>
                  <div className="mt-3 space-y-2">
                    {healthBreakdowns?.riskDomains?.length ? healthBreakdowns.riskDomains.map((item) => (
                      <div key={String(item.riskDomain)} className="rounded-2xl bg-white px-3 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-900">{String(item.riskDomain ?? "Sem domínio")}</p>
                          <Badge variant="outline">crítico {formatNumber(item.criticalRiskLives)}</Badge>
                        </div>
                        <p className="mt-1 text-xs leading-5 text-slate-600">{formatNumber(item.highRiskLives)} vidas em alto risco neste domínio.</p>
                      </div>
                    )) : (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-3 py-3 text-xs leading-5 text-slate-600">
                        {healthBreakdownsQuery.isLoading ? "Carregando riscos..." : "Nenhum domínio de risco consolidado neste recorte."}
                      </div>
                    )}
                  </div>
                </article>
              </div>
            </div>
          </article>

          <div className="space-y-4">
            <article className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Serviços visíveis</p>
              <div className="mt-3 space-y-2">
                {visibleModules.length ? visibleModules.map((item) => (
                  <div key={item.moduleKey} className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold text-slate-900">{MODULE_LABELS[item.moduleKey] || item.moduleKey.toUpperCase()}</p>
                      <Badge variant="outline">{item.userHasOperationalAccess ? "Pronto" : "Visível"}</Badge>
                    </div>
                  </div>
                )) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3 text-xs leading-5 text-slate-600">
                    Nenhum outro serviço visível foi encontrado para esta leitura agora.
                  </div>
                )}
              </div>
            </article>

            <article className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Cobertura analítica atual</p>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <div className="rounded-2xl bg-slate-50 px-3 py-3">
                  {healthContextQuery.isLoading
                    ? "Carregando disponibilidade analítica..."
                    : `${formatNumber(healthContextQuery.data?.scopedCompanies)} empresa(s), ${formatNumber(healthContextQuery.data?.scopedServices)} serviço(s) e ${formatNumber(healthContextQuery.data?.specialties?.length)} especialidade(s) no escopo atual.`}
                </div>
                <div className="rounded-2xl bg-slate-50 px-3 py-3">
                  {assistantEnabled
                    ? "O assistente está disponível por ícone para aprofundar a leitura quando necessário."
                    : "O assistente não está liberado para este contexto neste momento."}
                </div>
              </div>
            </article>

            <article className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Uso recomendado</p>
              <div className="mt-3 space-y-2 text-sm text-slate-700">
                <div className="rounded-2xl bg-slate-50 px-3 py-3">Use esta tela para leitura rápida do escopo, dos sinais de risco e dos serviços que mais concentram uso.</div>
                <div className="rounded-2xl bg-slate-50 px-3 py-3">Se precisar aprofundar, abra o assistente pelo ícone e faça perguntas curtas sobre a empresa e o período atual.</div>
              </div>
            </article>
          </div>
        </section>
      </div>
      <ModuleAIChatWidget enabled={assistantEnabled} />
    </SalviaLayout>
  );
}
